import {
  createCodeableConcept,
  createReference,
  getDisplayFromCodableConcepts,
  getFirstCodeFromCodableConcepts,
  medpraxSystem,
  type WithId,
} from '@quromedical/fhir-common';
import { first } from '@quromedical/utils';
import type { MedpraxPageResult } from '../external-integrations';
import { getValidReference } from '../mappings/reference';

import {
  type AllergyCreate,
  type AllergySeverity,
  type AllergyStatus,
  type AllergyWithId,
  AllergyWithIdSchema,
  type MedpraxAllergy,
} from './schema';

export const mapPageResultToMedpraxAllergy = (
  pageResult: MedpraxPageResult,
): MedpraxAllergy => {
  return {
    code: pageResult.Code,
    name: pageResult.Name,
    objectID: pageResult.objectID ? pageResult.objectID : '',
  };
};

export const mapAllergyToFhir = (
  allergy: AllergyWithId,
): WithId<fhir4.AllergyIntolerance> => {
  const {
    admission,
    type,
    patient,
    recorder,
    name,
    status,
    code,
    id,
    severity,
  } = allergy;

  const criticality = severity ? getAllergyCriticality(severity) : undefined;

  return {
    id: id,
    resourceType: 'AllergyIntolerance',
    type: 'allergy',
    code: createCodeableConcept(code, name, name, medpraxSystem),
    encounter: admission,
    clinicalStatus: createCodeableConcept(status),
    patient: patient,
    recorder: recorder,
    category: [type],
    criticality,
  };
};

export const mapFhirToAllergy = (
  allergy: fhir4.AllergyIntolerance,
): AllergyWithId => {
  const {
    encounter,
    category,
    patient,
    recorder,
    clinicalStatus,
    code,
    id,
    criticality,
  } = allergy;

  const mapped = {
    id,
    patient,
    name: code ? getDisplayFromCodableConcepts([code]) : undefined,
    code: code ? getFirstCodeFromCodableConcepts([code]) : undefined,
    admission: getValidReference(encounter),
    status: getFirstCodeFromCodableConcepts<AllergyStatus>(
      clinicalStatus ? [clinicalStatus] : undefined,
    ),
    recorder: getValidReference(recorder),
    type: category ? first(category) : undefined,
    severity: criticality ? allergySeverityMap[criticality] : undefined,
  };

  const parsed = AllergyWithIdSchema.safeParse(mapped);

  if (parsed.success == false) {
    throw parsed.error;
  }

  return parsed.data;
};

export const mapCreateAllergyToFhir = (
  allergy: AllergyCreate,
): fhir4.AllergyIntolerance => {
  const { admission, patient, recorder, name, code, severity } = allergy;

  const encounterReference = admission
    ? createReference('Encounter', admission)
    : undefined;
  const encounterFullUrlReference = admission
    ? { reference: admission }
    : undefined;
  const encounter = admission?.includes('urn')
    ? encounterFullUrlReference
    : encounterReference;
  const criticality = getAllergyCriticality(severity);

  return {
    resourceType: 'AllergyIntolerance',
    code: createCodeableConcept(code, name, name, medpraxSystem),
    encounter: encounter,
    clinicalStatus: createCodeableConcept('active'),
    patient: createReference('Patient', patient),
    recorder: recorder ? createReference('Practitioner', recorder) : undefined,
    type: 'allergy',
    category: ['medication'],
    criticality,
  };
};

export const mapUpdateAllergyToFhir = (
  allergy: AllergyWithId,
  status: AllergyStatus,
  severity: AllergySeverity,
): WithId<fhir4.AllergyIntolerance> => {
  return mapAllergyToFhir({
    ...allergy,
    status,
    severity,
  });
};

type Criticality = 'low' | 'high' | 'unable-to-assess';

const allergySeverityMap: Record<Criticality, AllergySeverity> = {
  low: 'mild',
  high: 'severe',
  'unable-to-assess': 'unknown',
};

const getAllergyCriticality = (
  severity: AllergySeverity,
): Criticality | undefined => {
  return (Object.keys(allergySeverityMap) as Criticality[]).find(
    (key) => allergySeverityMap[key] === severity,
  );
};
